import {useContext} from 'react';

import {animate, AnimatePresence} from '@sail/engine';
import {external} from '@sail/icons/react/Icon';
import {Icon, view} from '@sail/ui';

import type {ConversationButton} from '@stripe-internal/conversations-client';
import {useTheme} from '@stripe-internal/conversations-ui';

import {DotComChatBubbleAnimationContext} from '../useItemsDotCom';
import {dotComColors} from '../../theme/dotCom';

type Props = {
  button: ConversationButton;
  disabled?: boolean;
  messageId?: string;
  onPress: () => void;
};

export const DotComMessageButton = ({
  button,
  disabled,
  messageId,
  onPress,
}: Props) => {
  const {animatedMessageId} = useContext(DotComChatBubbleAnimationContext);
  const theme = useTheme();

  return (
    <AnimatePresence>
      <view.a
        data-testid="button-link"
        href={button.url}
        target={button.url}
        onClick={onPress}
        css={{
          transformOrigin: 'right bottom',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto',
          fontWeight: 400,
          border: '1px solid',
          borderColor: dotComColors.brand300,
          borderRadius: 'medium',
          background: dotComColors.brand,
          paddingX: '14px',
          paddingY: 'small',
          whiteSpace: 'pre-line',
          overflowWrap: 'break-word',
          color: dotComColors.brand600,
          width: 'fit-content',
          textAlign: 'start',
          transition: '150ms linear',
          ':hover': {
            color: theme.linkColor,
            borderColor: '#c4ccd8',
            boxShadow:
              '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
          },
        }}
        uses={[
          animate.in({
            duration: 250,
            delay: 300,
            opacity: 0,
            transform: 'translateX(20px)',
            easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
            disabled: disabled || messageId !== animatedMessageId,
          }),
        ]}
      >
        <Icon icon={external} />
        {button.title}
      </view.a>
    </AnimatePresence>
  );
};
